import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown, Loader, Message } from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import SearchParams from "simplydo/core/searchParams";
import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";

import api, { asQuery } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import TagDropdownChooser from "components/lib/Choosers/Tags/TagDropdownChooser";

import Board, { type IIdeaCard, type ILaneData } from "./Board";
import CustomLaneCard from "./CustomLaneCard";
import CustomLaneHeader from "./CustomLaneHeader";

const StyledProjectBoardContainer = styled.div`
  // The height of the board must be the viewports height minus the top bars
  height: calc(100vh - 205px);
  display: flex;
  flex-direction: column;
`;

const StyledBoard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 10px;
  > .row-flex {
    display: flex;
    align-items: center;
  }
  margin-top: -10px;
  flex-wrap: wrap;
  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: "Inter";
  }
  .ui.message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-family: "Inter";
    width: 40%;
    margin: 0 0 0 20px;
    padding: 0.5em 1em;
  }
`;

const StyledLaneContainer = styled.div`
  min-height: 200px;
  flex-grow: 1;
  font-family: "Inter";
  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: "Inter";
  }
  > p {
    margin-top: 20px;
    text-align: center;
    font-size: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 10px;
  > span {
    display: block;
    margin: 0 0 0 5px;
    font-weight: bold;
  }
`;

const FilterHeight = "35px";

const StyledBoardHeader = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isComputer ? "row" : "column")};
  > .filter-container {
    display: flex;
    flex-direction: ${({ theme }) =>
      theme.sizes.isComputer ? "row" : "column"};
  }
  .header {
    display: flex;
    flex-direction: row;
    align-items: center;
    ${({ theme }) =>
      !theme.sizes.isComputer ?
        `
      margin-bottom: 5px;
    `
      : ""}
    height: ${FilterHeight};
  }
  .filters {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex: 1;
    margin-top: 10px;
    height: ${FilterHeight};
  }
  ${({ theme }) =>
    !theme.sizes.isComputer ?
      `
    gap: 5px;
  `
    : `
    flex-wrap: wrap;
    justify-content: space-between;
    margin: -3px 0 0 0;
    > * {
      margin: 3px;
      &:last-child {
        margin-right: 0;
      }
    }
  `}
  ${({ theme }) =>
    !theme.sizes.isMobile ?
      `
    .header {
      flex: 0;
      min-width: fit-content;
    }
  `
    : `
    .header {
      margin-bottom: 5px;
    }
  `}
`;

type IProjectBoardProps = {
  forId: string;
  ideas: OpenAPI.Schemas["Idea"][];
  ideasTotal: number;
  ideasLoading: boolean;
  laneImpactMeasures: NonNullable<
    OpenAPI.GET<"/challenges/{id}/ideas">["response"]["laneImpactMeasures"]
  >;
  defaultLane: NonNullable<
    OpenAPI.GET<"/challenges/{id}">["response"]["projectBoard"]
  >["defaultLane"];

  laneFilter?: string[];
  setPreviewId: (ideaId: string) => void;
  highlightCard?: string;

  handleWebsocketLanes: () => void;
  handleWebsocketIdeas: () => void;
  canManageChallenge?: boolean;

  checkUpdateProjectLane: (
    ideaId: string,
    targetLaneId: string,
    position: number,
  ) => void;
  checkUpdateProjectLaneMulti: (ideaIds: string[], targetLane: string) => void;
  setSetupModalOpen: Dispatch<SetStateAction<boolean>>;

  selectionMode?: string;
  selectedIdeas: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"];
  setSelectedIdeas: Dispatch<
    SetStateAction<OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"]>
  >;
};

const ProjectBoard = (props: IProjectBoardProps) => {
  const {
    forId,
    ideas,
    ideasTotal,
    ideasLoading,
    laneImpactMeasures,
    defaultLane,
    laneFilter,
    setPreviewId,
    highlightCard,
    handleWebsocketLanes,
    handleWebsocketIdeas,
    canManageChallenge,
    checkUpdateProjectLane,
    checkUpdateProjectLaneMulti,
    setSetupModalOpen,
    selectionMode,
    selectedIdeas,
    setSelectedIdeas,
  } = props;
  const user = useAppSelector((state) => state.user);
  const [laneData, setLaneData] = useState<ILaneData[]>([]);
  const totalIdeas = ideas?.length || 0;
  const theme = useTheme();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const queryParams = useMemo(() => new SearchParams(search), [search]);
  const ideaFilter = useMemo(
    () => queryParams.get("ideaFilter") || "",
    [queryParams],
  );
  const ideaView = useMemo(
    () => queryParams.get("ideaView") || "",
    [queryParams],
  );
  const queryTags = useMemo(() => queryParams.get("tags") || "", [queryParams]);
  const queryTagsArray = useMemo(() => queryTags.split(","), [queryTags]);

  const challengeQuery = useQuery({
    queryKey: ["challenges", forId],
    queryFn: asQuery(api.challenges.get, { params: [forId] }),
  });
  const challenge = challengeQuery.data;

  const canViewProjectBoard = useMemo(
    () =>
      util.hasPermission(user, "challenge.viewProjectBoard", challenge?._id) ||
      challenge?.projectManagementVisibility === "users",
    [user, challenge?.projectManagementVisibility, challenge?._id],
  );

  const projectLaneQuery = useQuery({
    queryKey: ["boards", forId, "lanes"],
    queryFn: asQuery(api.boards.getProjectLanes, { params: [forId!] })!,
    enabled: canManageChallenge || canViewProjectBoard,
  });
  const lanes = useMemo(
    () => projectLaneQuery.data?.lanes || [],
    [projectLaneQuery.data?.lanes],
  );

  const selectedTags = useMemo(
    () =>
      challenge?.tagList?.filter((tag) => queryTagsArray.includes(tag._id)) ||
      [],
    [queryTagsArray, challenge],
  );

  const isMultiSelect = useMemo(
    () => selectionMode === "multi",
    [selectionMode],
  );

  const onSelectIdeas = useCallback(
    (ideas: OpenAPI.Schemas["Idea"][], toSelect: boolean) => {
      if (toSelect) {
        setSelectedIdeas((prev) => {
          const newIdeas = ideas.filter(
            (idea) => !prev.find((i) => i._id === idea._id),
          );
          return [...prev, ...newIdeas];
        });
      } else {
        setSelectedIdeas((prev) =>
          prev.filter((idea) => !ideas.map((i) => i._id).includes(idea._id)),
        );
      }
    },
    [setSelectedIdeas],
  );

  const removeTag = useCallback(
    (tag) => {
      if (!queryTagsArray.includes(tag._id)) {
        return;
      }
      queryTagsArray.splice(queryTagsArray.indexOf(tag._id), 1);
      queryParams.set("tags", queryTagsArray.join(","));
      navigate({ ...location, search: queryParams.toString() });
    },
    [queryTagsArray, queryParams, navigate, location],
  );

  const addTag = useCallback(
    (tag) => {
      if (queryTagsArray.includes(tag._id)) {
        return;
      }
      queryTagsArray.push(tag._id);
      queryParams.set("tags", queryTagsArray.join(","));
      navigate({ ...location, search: queryParams.toString() });
    },
    [queryTagsArray, queryParams, navigate, location],
  );

  const setIdeaFilter = useCallback(
    (filter) => {
      if (filter.length === 0) {
        queryParams.delete("ideaFilter");
      } else {
        queryParams.set("ideaFilter", filter);
      }
      navigate({ ...location, search: queryParams.toString() });
    },
    [navigate, queryParams, location],
  );

  const setIdeaView = useCallback(
    (view) => {
      queryParams.set("ideaView", view);
      navigate({ ...location, search: queryParams.toString() });
    },
    [navigate, queryParams, location],
  );

  const getLaneData = useCallback(() => {
    const cards: IIdeaCard[] = ideas
      ?.map((idea) => {
        const board = idea?.projectManagement?.boards?.find(
          (b) => b.forId === idea.challenge,
        );
        const ret: IIdeaCard = {
          id: idea._id!,
          idea,
          lane: board?.lane,
          position: board?.order || 0,
        };
        if (board?.lane) {
          const cardLane = lanes.find((l) => l._id === board.lane);
          ret.laneMetaType = cardLane?.metaType || "normal";
        }
        if (
          !canManageChallenge &&
          !util.hasPermission(
            user,
            "challenge.moveProjectIdeas",
            challenge?._id,
          ) &&
          !(board?.assignees?.indexOf(user._id) > -1)
        ) {
          ret.draggingDisabled = true;
        }
        return ret;
      })
      .sort((a, b) => (a.position > b.position ? 1 : -1));

    const newLanes: ILaneData[] =
      !laneFilter?.length || laneFilter.includes("default") ?
        [
          {
            id: "default",
            title: defaultLane?.name || "Default",
            description: defaultLane?.description || "",
            label: "Default",
            cards: cards.filter((i) => !i.lane),
            draggingDisabled: true,
          },
        ]
      : [];
    lanes.forEach((l) => {
      newLanes.push({
        id: l._id!,
        title: l.name,
        description: l.description,
        metaType: l.metaType,
        cards: cards.filter((i) => i.lane === l._id),
      });
    });
    const ideaOrder: string[] = [];
    newLanes.forEach((lane) => {
      const { cards: newCards = [] } = lane;
      newCards.forEach((card) => {
        const { idea } = card;
        ideaOrder.push(idea._id);
      });
    });
    setLaneData(newLanes);
  }, [
    ideas,
    laneFilter,
    defaultLane?.name,
    defaultLane?.description,
    lanes,
    canManageChallenge,
    user,
    challenge?._id,
  ]);

  useEffect(() => {
    getLaneData();
  }, [getLaneData]);

  const createLane = useCallback(() => {
    const unnamedLanes = lanes.filter(
      (l) => (l.name || "").indexOf("New lane ") > -1,
    );
    api.boards.createProjectLane(
      forId,
      { name: `New lane ${unnamedLanes.length + 1}` },
      (lane) => {
        api.queryClient.setQueryData(
          ["boards", forId, "lanes"],
          (oldData: any) => {
            return {
              ...(oldData || {}),
              lanes: [...(oldData?.lanes || []), lane],
            };
          },
        );
        handleWebsocketLanes();
      },
      (err) => toast.error(err.message),
    );
  }, [forId, handleWebsocketLanes, lanes]);

  const ideaStatusText = useMemo(
    () => ({
      all: `All ${t("generic.ideas")}`,
      me: `${t("common:capitalise", { key: "generic.ideas" })} assigned to me`,
      unassigned: `${t("common:capitalise", { key: "generic.ideas" })} that are unassigned`,
    }),
    [t],
  );

  const IdeaFiltersContent = useMemo(
    () => (
      <>
        <Input
          icon="search"
          iconPosition="left"
          placeholder={`Filter ${ideasTotal} ${t("generic.ideas")} by name or author...`}
          value={ideaFilter}
          onChange={(e) => setIdeaFilter(e.target.value)}
          style={{ height: FilterHeight, width: 300 }}
          size="small"
        />
        <TagDropdownChooser
          tags={selectedTags}
          availableTags={challenge?.tagList?.filter(
            (tag) => !tag?.disabledEntities?.includes("ideas"),
          )}
          addTag={addTag}
          removeTag={removeTag}
          showCount={false}
          style={{
            marginLeft: 5,
            height: FilterHeight,
            minHeight: 0,
            fontSize: ".92857143em",
          }}
          onStateChange={undefined}
          size="small"
        />
        <Dropdown
          icon={null}
          style={{ marginLeft: 5, height: FilterHeight }}
          direction="left"
          trigger={
            <Button
              basic
              content={`Showing ${ideaStatusText[ideaView || "all"].toLowerCase()}`}
              icon="dropdown"
              style={{ height: FilterHeight }}
              size="small"
            />
          }
        >
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setIdeaView("all")}>
              Show all {t("generic.ideas")}
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setIdeaView("me")}>
              Show {t("generic.ideas")} assigned to me
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setIdeaView("unassigned")}>
              Show {t("generic.ideas")} that are unassigned
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </>
    ),
    [
      challenge,
      ideasTotal,
      ideaFilter,
      selectedTags,
      addTag,
      removeTag,
      ideaStatusText,
      ideaView,
      setIdeaFilter,
      setIdeaView,
      t,
    ],
  );

  const canSetupLanes = useMemo(
    () =>
      (canManageChallenge ||
        util.hasPermission(
          user,
          "challenge.editProjectBoard",
          challenge?._id,
        )) &&
      !lanes.length,
    [canManageChallenge, challenge?._id, user, lanes.length],
  );
  const browserIsSupported = useMemo(() => util.browserIsSupported(), []);
  const showHeaderContent = useMemo(
    () => canSetupLanes || !browserIsSupported,
    [browserIsSupported, canSetupLanes],
  );

  const BoardHeaderContent = useMemo(
    () => (
      <>
        {canSetupLanes ?
          <Button
            icon="cogs"
            size="small"
            content="Setup lanes"
            onClick={() => setSetupModalOpen(true)}
            style={{ marginLeft: 5, height: FilterHeight }}
          />
        : null}
        {!browserIsSupported && (
          <Message warning icon="info circle">
            <Message.Header>Unsupported browser</Message.Header>
            <p>
              {util.appName()} is unable to display the project board because
              your browser does not support the required features. We recommend
              updating to a more modern browser, such as a newer version of
              Edge, Firefox or Chrome.
            </p>
          </Message>
        )}
      </>
    ),
    [browserIsSupported, canSetupLanes, setSetupModalOpen],
  );

  return (
    <StyledProjectBoardContainer>
      {showHeaderContent ?
        <StyledBoard>
          <StyledBoardHeader>
            {theme.sizes.isComputer ?
              <>
                <div className="filter-container">
                  <div className="filters">{IdeaFiltersContent}</div>
                </div>
                <div className="filter-container">
                  <div className="filters">{BoardHeaderContent}</div>
                </div>
              </>
            : <div className="filter-container">
                <div className="filters">
                  {IdeaFiltersContent}
                  {BoardHeaderContent}
                </div>
              </div>
            }
          </StyledBoardHeader>
        </StyledBoard>
      : null}
      <StyledLaneContainer>
        {projectLaneQuery.isLoading || !challengeQuery.isSuccess ?
          <LoadingContainer>
            <Loader active inline size="small" />
            <span>Loading project board...</span>
          </LoadingContainer>
        : <>
            {!ideas?.length && !ideasLoading ?
              <Message
                style={{ margin: 10 }}
                info
                header={`No ${t("generic.ideas")} found`}
                content={`Please adjust your filters or wait until ${t("generic.ideas")} are added to the ${t("generic.challenge")}.`}
              />
            : null}
            <Board
              lanes={laneData}
              setLanes={setLaneData}
              onCardMove={(cardMove) => {
                checkUpdateProjectLane(
                  cardMove.draggableId.split("-")[1],
                  cardMove.toDroppable.split("-")[1],
                  cardMove.toIndex,
                );
              }}
              onLaneMove={(laneMove) => {
                const selectedLane = lanes[laneMove.fromIndex - 1];

                // Better UX if we update before API call, then reset if it fails
                api.boards.updateProjectLane(
                  forId,
                  selectedLane._id,
                  { order: laneMove.toIndex - 1 },
                  () => {
                    handleWebsocketLanes();
                  },
                  () => {
                    toast.error("Failed to move lane");
                  },
                );
              }}
              createLane={
                (
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editProjectBoard",
                    challenge?._id,
                  )
                ) ?
                  createLane
                : undefined
              }
              highlightCard={highlightCard}
              renderLaneHeader={(lane) => {
                const { cards = [] } = lane;
                const allIdeasSelected =
                  cards.length > 0 &&
                  cards.every(
                    (card) =>
                      !!selectedIdeas.find((sidea) => sidea === card.idea._id),
                  );
                return (
                  <CustomLaneHeader
                    forId={forId}
                    lane={lane}
                    ideas={ideas}
                    lanes={lanes}
                    totalIdeas={totalIdeas}
                    user={user}
                    handleWebsocketLanes={handleWebsocketLanes}
                    handleWebsocketIdeas={handleWebsocketIdeas}
                    canManageChallenge={canManageChallenge}
                    laneImpactMeasures={laneImpactMeasures[lane.id] ?? []}
                    checkUpdateProjectLaneMulti={checkUpdateProjectLaneMulti}
                    isSelecting={isMultiSelect}
                    isSelected={allIdeasSelected}
                    onSelect={() => {
                      if (allIdeasSelected) {
                        onSelectIdeas(
                          cards.map((card) => card.idea),
                          false,
                        );
                      } else {
                        onSelectIdeas(
                          cards.map((card) => card.idea),
                          true,
                        );
                      }
                    }}
                  />
                );
              }}
              renderCard={(card) => (
                <CustomLaneCard
                  card={card}
                  challenge={challengeQuery.data}
                  user={user}
                  loading={ideasLoading}
                  onClick={() => setPreviewId(card.idea._id)}
                  highlightCard={highlightCard}
                  isSelecting={isMultiSelect}
                  onSelect={(idea) =>
                    onSelectIdeas(
                      [idea],
                      !selectedIdeas.find((i) => i._id === idea._id),
                    )
                  }
                  isSelected={
                    selectedIdeas.find((i) => i._id === card.idea._id) !==
                    undefined
                  }
                />
              )}
            />
          </>
        }
      </StyledLaneContainer>
    </StyledProjectBoardContainer>
  );
};

export default ProjectBoard;
